<script setup lang="ts">
import { computed, type PropType, ref, toRef, watch } from 'vue';
import Dialog from 'primevue/dialog';
import { useDragAndDrop } from '@formkit/drag-and-drop/vue';
import { animations } from '@formkit/drag-and-drop';
import { ButtonType, CustomButton, MatrixConfigRowColumn, MatrixColumnConfig, MatrixDropdownConfigDialog } from '@/components';
import { MatrixColumnType, MatrixMode, type MatrixRow, type DynamicRowConfig, type FormField, type MatrixColumn } from '@/models';
import { cloneDeep } from 'lodash';
import Checkbox from 'primevue/checkbox';
import MatrixRowConfigDialog from './MatrixRowConfigDialog.vue';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  field: {
    type: Object as PropType<FormField>,
    required: true
  }
});
const initialField = toRef<FormField>(props.field);
const localField = ref<FormField>(cloneDeep(props.field));
const [rowParent, rows] = useDragAndDrop(localField.value.options!.rows!, {
  plugins: [animations()]
});
const [columnParent, columns] = useDragAndDrop(
  localField.value.options!.columns!,
  { plugins: [animations()] }
);
const rankMode = ref(props.field.options?.mode === MatrixMode.RANK);
const showMatrixRowConfigDialog = ref(false);
const selectedRowForConfig = ref<MatrixRow>();

const emits = defineEmits(['onClose', 'onSave']);

const closeDialog = () => {
  emits('onClose');
};

const onAddRow = () => {
  const tempRows = cloneDeep(rows.value);
  tempRows.sort((a, b) => a.id - b.id);
  const id = tempRows.length > 0 ? tempRows[tempRows.length - 1].id + 1 : 1;
  rows.value.push({
    id,
    label: ''
  });
};

const onRowDelete = (index: number) => {
  rows.value.splice(index, 1);
};

const onAddColumn = () => {
  // this will ensure the ids are not duplicated anymore
  const tempColumns = cloneDeep(columns.value);
  tempColumns.sort((a, b) => a.id - b.id);
  const id = tempColumns.length > 0 ? tempColumns[tempColumns.length - 1].id + 1 : 1;

  columns.value.push({
    id,
    label: '',
    type: MatrixColumnType.TEXT,
    tintColor: ''
  });
};

const onColumnDelete = (index: number) => {
  columns.value.splice(index, 1);
};

const openDropdownChoicesConfig = ref(false);
const selectedColumnForChoicesConfig = ref<MatrixColumn>();
const handleDropdownChoicesDialog = (
  column?: MatrixColumn,
  close?: boolean
) => {
  if (!close) {
    selectedColumnForChoicesConfig.value = columns.value.find(
      fColumn => fColumn.id == column!.id
    );
  }
  openDropdownChoicesConfig.value = !openDropdownChoicesConfig.value;
};

const onColumnConfigUpdate = (updatedColumn: MatrixColumn) => {
  const columnIndex = columns.value.findIndex(
    column => column.id === updatedColumn.id
  );
  if (columnIndex > -1) {
    const updatedColumns = [...columns.value];
    updatedColumns[columnIndex] = updatedColumn;
    columns.value = updatedColumns;
  }
};

const isPristine = computed(
  () =>
    JSON.stringify(initialField.value.options.rows) !==
      JSON.stringify(rows.value) ||
    JSON.stringify(initialField.value.options.columns) !==
      JSON.stringify(columns.value) || 
    initialField.value.options.mode !== (rankMode.value ? MatrixMode.RANK : undefined)
);

const saveConfig = () => {
  initialField.value.options.rows = rows.value;
  initialField.value.options.columns = columns.value;
  initialField.value.options.mode = rankMode.value ? MatrixMode.RANK : undefined;
  closeDialog();
};

const areAllColumnsSingleChoice = computed(() => {
  if (!columns.value || columns.value.length === 0) {
    return false;
  }
  return columns.value.every(
    column => column.type === MatrixColumnType.SINGLE_CHOICE
  );
});

const onRankModeChange = () => {
  if (rankMode.value) {
    localField.value.options!.mode = MatrixMode.RANK;
  } else {
    localField.value.options!.mode = undefined;
  }
};

watch(areAllColumnsSingleChoice, newValue => {
  if (!newValue) {
    rankMode.value = false;
    localField.value.options!.mode = undefined;
  }
});

const onRowOptionsClick = (index: number) => {
  selectedRowForConfig.value = rows.value[index];
  showMatrixRowConfigDialog.value = true;
};

const onRowConfigSave = (dynamicRowConfig: DynamicRowConfig) => {
  showMatrixRowConfigDialog.value = false;
  const selectedRow = selectedRowForConfig.value;
  if (!selectedRow) {
    return;
  }
  const rowToUpdate = rows.value.find(row => row.id === selectedRow.id);
  if (!rowToUpdate) {
    return;
  }
  rowToUpdate.dynamicRowConfig = dynamicRowConfig;
};
</script>

<template>
  <div class="dialog-root">
    <Dialog :visible="visible"
      header="Matrix settings"
      modal
      class="dialog"
      append-to="self"
      @update:visible="closeDialog">
      <div class="dialog-content">
        <div class="matrix-rows">
          <h4 class="title">Rows</h4>
          <div class="rows-parent" ref="rowParent">
            <div v-for="(row, index) in rows" :key="row.id" class="row-item">
              <MatrixConfigRowColumn :index="index"
                v-model="row.label"
                :row="true"
                @onOptionsClick="onRowOptionsClick"
                @onDelete="onRowDelete"/>
            </div>
          </div>
          <div class="add-button">
            <CustomButton text="Add row"
              :icon="{ style: 'fas', icon: 'plus' }"
              @onClick="onAddRow"
              :type="ButtonType.OUTLINED"/>
          </div>
        </div>
        <div class="matrix-columns">
          <h4 class="title">Columns</h4>
          <div class="columns-parent" ref="columnParent">
            <div v-for="(column, index) in columns"
              :key="column.id"
              class="row-item">
              <MatrixConfigRowColumn :index="index"
                v-model="column.label"
                @onDelete="onColumnDelete"/>
            </div>
          </div>
          <div class="add-button">
            <CustomButton text="Add column"
              :icon="{ style: 'fas', icon: 'plus' }"
              @onClick="onAddColumn"
              :type="ButtonType.OUTLINED"/>
          </div>
        </div>
        <div class="column-config">
          <h4 class="title">Columns Settings</h4>
          <div class="rank-mode" v-if="areAllColumnsSingleChoice">
            <Checkbox v-model="rankMode"
              inputId="rank-mode"
              name="rank-mode"
              @change="onRankModeChange"
              :binary="true"/>
            <div class="group-header">Rank mode</div>
          </div>
          <div v-for="column in columns"
            :key="column.id"
            class="column-config-item">
            <MatrixColumnConfig :column="column"
              v-if="column.label != ''"
              @onDropdownChoicesClick="handleDropdownChoicesDialog"
              @onColumnUpdate="onColumnConfigUpdate"/>
          </div>
        </div>
      </div>
      <div class="footer">
        <div class="empty"></div>
        <div class="footer-buttons">
          <CustomButton text="Cancel"
            @onClick="closeDialog"
            :margin-left="16"
            :type="ButtonType.OUTLINED"/>
          <CustomButton text="Save"
            @onClick="saveConfig"
            :disabled="!isPristine"
            :margin-left="16"
            :type="ButtonType.POSITIVE"/>
        </div>
      </div>
    </Dialog>
  </div>

  <MatrixRowConfigDialog :visible="showMatrixRowConfigDialog"
    v-if="showMatrixRowConfigDialog"
    :row="selectedRowForConfig"
    @onSave="onRowConfigSave"
    @onClose="showMatrixRowConfigDialog = false"/>
  <MatrixDropdownConfigDialog :visible="openDropdownChoicesConfig"
    v-if="openDropdownChoicesConfig"
    :column="selectedColumnForChoicesConfig"
    @onClose="handleDropdownChoicesDialog(undefined, true)"/>
</template>

<style scoped lang="scss">
@import "../../../../assets/dialog";
:deep(.p-dialog) {
  max-height: 80vh;
}
:deep(.p-dialog-header) {
  padding: unset;
  padding: 40px 40px 0px 40px;
}
:deep(.p-dialog-content) {
  padding: unset;
}
.dialog-content {
  width: 1000px;
  padding: 8px 40px 40px 40px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 40px;
  overflow: auto;

  .matrix-rows,
  .matrix-columns,
  .column-config {
    flex: 1;
  }

  .add-button {
    display: flex;
    width: 210px;
    justify-content: center;
    margin-top: 16px;
  }

  .title {
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 600;
  }
}
.footer {
  padding: 12px 40px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #ced3d9;
  background-color: #f5f7f7;
  position: absolute;
  bottom: 0;
  width: 100%;
  z-index: 9999;

  .footer-buttons {
    display: flex;
  }
}
.rank-mode {
  display: flex;
  align-items: center;
  margin-bottom: 16px;

  .group-header {
    margin-left: 8px;
    font-size: 14px;
  }
}
</style>
